import { ComplianceStateDisplayMap, ComplianceStateEnum } from "src/app/enums/complianceState.enum";
import {
    RiskRegisterCardColorScale,
    RiskRegisterCardSlotOption,
    RiskRegisterCardSlotOptionLabels,
} from "src/app/enums/riskRegisterCardSlot.enum";
import { RiskRegisterViewMode } from "src/app/enums/riskRegisterViewMode.enum";
import {
    CardPreviewSource,
    RiskRegisterCard,
    RiskRegisterCardInputs,
    RiskRegisterCardLabels,
    RiskRegisterCardSlotKey,
    RiskRegisterCardSlotValue,
    RiskRegisterCardSlots,
} from "src/app/models/risk-register-card.model";
import { RiskRegisterViewPageSettings } from "src/app/models/risk-register-matrix.model";
import nameOf from "src/app/shared/form/nameOf.helper";
import { RiskRegisterCapabilityProfile } from "./risk-register-capability-profile";

const OPTION = RiskRegisterCardSlotOption;

const VIEW_MODE_TO_OPTION: Record<RiskRegisterViewMode, RiskRegisterCardSlotOption> = {
    [RiskRegisterViewMode.ExposureBand]: OPTION.ExposureBand,
    [RiskRegisterViewMode.Likelihood]: OPTION.Likelihood,
    [RiskRegisterViewMode.HealthRisk]: OPTION.HealthRisk,
};

export const CARD_SLOT_OPTIONS: RiskRegisterCardSlotOption[] = Object.values(OPTION);

export function slotOptionForViewMode(viewMode: RiskRegisterViewMode): RiskRegisterCardSlotOption {
    return VIEW_MODE_TO_OPTION[viewMode];
}

export function viewModeForSlotOption(
    option: RiskRegisterCardSlotOption,
): RiskRegisterViewMode | null {
    return Object.values(RiskRegisterViewMode).find((mode) => VIEW_MODE_TO_OPTION[mode] === option) ?? null;
}

export function editableCardSlotKeys(profile: RiskRegisterCapabilityProfile): RiskRegisterCardSlotKey[] {
    return profile.usesHealthRiskScore
        ? ["main", "below", "topRight"]
        : ["main", "below", "topRight", "bottomRight"];
}

export function translateCardSlotOptionLabels(
    translate: (key: string) => string,
): Record<RiskRegisterCardSlotOption, string> {
    return Object.fromEntries(
        CARD_SLOT_OPTIONS.map((option) => [option, translate(RiskRegisterCardSlotOptionLabels[option])]),
    ) as Record<RiskRegisterCardSlotOption, string>;
}

export function defaultCardSlots(
    profile: RiskRegisterCapabilityProfile,
    viewMode: RiskRegisterViewMode,
): RiskRegisterCardSlots {
    if (profile.usesHealthRiskScore) {
        return {
            main: OPTION.None,
            below: OPTION.None,
            topRight: OPTION.Status,
            bottomRight: OPTION.None,
        };
    }
    return {
        main: slotOptionForViewMode(viewMode),
        below: OPTION.NumberOfSamples,
        topRight: OPTION.Status,
        bottomRight: OPTION.PostTreatmentScore,
    };
}

const CARD_SLOTS_KEY = nameOf<RiskRegisterViewPageSettings>((x) => x.cardSlots);

function isCardSlotOption(value: unknown): value is RiskRegisterCardSlotOption {
    return typeof value === "string" && CARD_SLOT_OPTIONS.some((option) => option === value);
}

export function cardSlotsFromPageSettings(
    pageSettings: Record<string, unknown> | null | undefined,
    defaults: RiskRegisterCardSlots,
    editableSlots: RiskRegisterCardSlotKey[],
): RiskRegisterCardSlots {
    const saved = pageSettings?.[CARD_SLOTS_KEY];
    if (!saved || typeof saved !== "object") {
        return { ...defaults };
    }
    return editableSlots.reduce<RiskRegisterCardSlots>((slots, key) => {
        const value: unknown = Reflect.get(saved, key);
        return isCardSlotOption(value) ? { ...slots, [key]: value } : slots;
    }, { ...defaults });
}

export function cardSlotsPageSettingsPatch(slots: RiskRegisterCardSlots): Record<string, unknown> {
    return { [CARD_SLOTS_KEY]: slots };
}

export function writeCardSlotsPageSettings(
    pageSettings: Record<string, unknown>,
    slots: RiskRegisterCardSlots,
    defaults: RiskRegisterCardSlots,
): Record<string, unknown> {
    const next = { ...pageSettings };
    const isDefault =
        slots.main === defaults.main &&
        slots.below === defaults.below &&
        slots.topRight === defaults.topRight &&
        slots.bottomRight === defaults.bottomRight;
    if (isDefault) {
        delete next[CARD_SLOTS_KEY];
    } else {
        next[CARD_SLOTS_KEY] = { ...slots };
    }
    return next;
}

function colorScaleForOption(
    option: RiskRegisterCardSlotOption,
    healthMode: boolean,
): RiskRegisterCardColorScale | null {
    switch (option) {
        case OPTION.ExposureBand:
        case OPTION.Likelihood:
            return RiskRegisterCardColorScale.Likelihood;
        case OPTION.HealthRisk:
        case OPTION.RiskRating:
            return RiskRegisterCardColorScale.RiskLevel;
        case OPTION.En689Compliance:
            return RiskRegisterCardColorScale.Compliance;
        case OPTION.PostTreatmentScore:
            return healthMode
                ? RiskRegisterCardColorScale.RiskLevel
                : RiskRegisterCardColorScale.Likelihood;
        default:
            return null;
    }
}

export function cardColorScalesInUse(
    slots: RiskRegisterCardSlots,
    profile: RiskRegisterCapabilityProfile,
    healthMode: boolean,
): Set<RiskRegisterCardColorScale> {
    const scales = new Set<RiskRegisterCardColorScale>();
    const mainScale = colorScaleForOption(slots.main, healthMode);
    if (mainScale) {
        scales.add(mainScale);
    } else if (profile.usesHealthRiskScore) {
        scales.add(RiskRegisterCardColorScale.RiskLevel);
    }
    if (!profile.usesHealthRiskScore) {
        const cornerScale = colorScaleForOption(slots.bottomRight, healthMode);
        if (cornerScale) {
            scales.add(cornerScale);
        }
    }
    return scales;
}

function colorForOption(option: RiskRegisterCardSlotOption, inputs: RiskRegisterCardInputs): string | null {
    if (inputs.isHazardNotApplicable) {
        return inputs.greyColor;
    }
    switch (option) {
        case OPTION.ExposureBand:
        case OPTION.Likelihood:
            return inputs.likelihoodColor;
        case OPTION.HealthRisk:
        case OPTION.RiskRating:
            return inputs.healthRiskColor;
        case OPTION.En689Compliance:
            return ComplianceStateDisplayMap[inputs.complianceState ?? ComplianceStateEnum.NotAssessed]?.color
                ?? inputs.greyColor;
        case OPTION.PostTreatmentScore:
            return inputs.residualColor;
        default:
            return null;
    }
}

function valueOrNotAvailable(value: string | number | null | undefined, notAvailable: string): string {
    return value == null || value === "" ? notAvailable : value.toString();
}

function slotText(
    option: RiskRegisterCardSlotOption,
    inputs: RiskRegisterCardInputs,
    labels: RiskRegisterCardLabels,
): string {
    const na = labels.notAvailable;
    switch (option) {
        case OPTION.ExposureBand:
            return inputs.exposureBand || na;
        case OPTION.Likelihood:
            return inputs.likelihoodName || na;
        case OPTION.HealthRisk:
            return inputs.healthScore != null ? inputs.healthRiskLevelName ?? na : na;
        case OPTION.RiskRating:
            return valueOrNotAvailable(inputs.healthScore, na);
        case OPTION.En689Compliance: {
            const display = ComplianceStateDisplayMap[inputs.complianceState ?? ComplianceStateEnum.NotAssessed];
            return display ? labels.translate(display.label) : na;
        }
        case OPTION.ConsequenceRating:
            return valueOrNotAvailable(inputs.consequenceRating, na);
        case OPTION.PostTreatmentScore:
            return valueOrNotAvailable(inputs.residualScore, na);
        case OPTION.NumberOfSamples:
            return valueOrNotAvailable(inputs.numberOfSamples, na);
        default:
            return "";
    }
}

export function resolveCardSlot(
    option: RiskRegisterCardSlotOption,
    inputs: RiskRegisterCardInputs,
    labels: RiskRegisterCardLabels,
): RiskRegisterCardSlotValue | null {
    if (option === OPTION.None) {
        return null;
    }
    if (option === OPTION.Status) {
        return {
            text: "",
            tooltip: labels.translate(inputs.statusName ?? ""),
            iconSrc: inputs.statusSource ?? "",
        };
    }
    if (inputs.isHazardNotApplicable) {
        return { text: "", tooltip: "" };
    }
    const text = slotText(option, inputs, labels);
    if (option === OPTION.PostTreatmentScore) {
        return { text, tooltip: labels.translate("Residual Likelihood Score") };
    }
    const prefix =
        option === OPTION.HealthRisk ? labels.healthRiskRatingTooltip : labels.optionLabels[option];
    return { text, tooltip: `${prefix}: ${text}` };
}

export function cardPreviewRichness(register: CardPreviewSource): number {
    return [
        register.healthRiskScore != null,
        register.qualitativeLikelihoodScore != null || register.quantitativeLikelihoodScore != null,
        register.complianceState != null && register.complianceState !== ComplianceStateEnum.NotAssessed,
        (register.quantitativeNumberOfSamples ?? 0) > 0,
        register.residualHealthRisk != null || register.residualLikelihoodScore != null,
        register.consequenceRating != null,
    ].filter(Boolean).length;
}

export function pickCardPreviewSample<T extends CardPreviewSource>(registers: T[]): T | null {
    return registers.reduce<T | null>(
        (best, register) => (!best || cardPreviewRichness(register) > cardPreviewRichness(best) ? register : best),
        null,
    );
}

export function buildRegisterCard(
    slots: RiskRegisterCardSlots,
    inputs: RiskRegisterCardInputs,
    labels: RiskRegisterCardLabels,
    profile: RiskRegisterCapabilityProfile,
): RiskRegisterCard {
    const fallbackColor = profile.usesHealthRiskScore
        ? inputs.isHazardNotApplicable
            ? inputs.greyColor
            : inputs.healthRiskColor
        : inputs.greyColor;
    return {
        main: resolveCardSlot(slots.main, inputs, labels),
        below: resolveCardSlot(slots.below, inputs, labels),
        topRight: resolveCardSlot(slots.topRight, inputs, labels),
        bottomRight: resolveCardSlot(slots.bottomRight, inputs, labels),
        color: colorForOption(slots.main, inputs) ?? fallbackColor,
        cornerColor: colorForOption(slots.bottomRight, inputs) ?? inputs.greyColor,
    };
}
